import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
// @mui
import {
  Box,
  Card,
  Grid,
  Stack,
  Button,
  Table,
  Divider,
  Container,
  TextField,
  TableBody,
  Typography,
  TableCell,
  TableRow,
  TableHead,
  LinearProgress,
  InputAdornment,
  TableContainer,
  TablePagination,
} from '@mui/material';
// components
import Page from 'src/components/Page';
import Label from 'src/components/Label';
import Iconify from 'src/components/Iconify';
import Scrollbar from 'src/components/Scrollbar';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import {
  TableNoData,
  ColumnsButton,
  TableSkeleton,
  TableHeadCustom,
} from 'src/components/table';
import useTable, { getComparator } from 'src/hooks/useTable';
import useColumnVisibility, { ColumnDef } from 'src/hooks/useColumnVisibility';
import { PATH_DASHBOARD } from 'src/routes/paths';
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';
import { formatDay } from '../landing/rules/format';
import {
  BatchListRow,
  BatchStateColor,
  batchStateChip,
  lastDayToJoin,
} from '../batch-management/batchState';
import AttendanceMatrixCard from './AttendanceMatrixCard';
import RecordedProgressCard from './RecordedProgressCard';

// ----------------------------------------------------------------------

const STUDENT_COLUMNS: ColumnDef[] = [
  { id: 'name', label: 'Student', align: 'left' },
  { id: 'email', label: 'Email', align: 'left' },
  { id: 'phone', label: 'Phone', align: 'left' },
  { id: 'batch', label: 'Batch', align: 'left' },
  { id: 'progress', label: 'Attendance', align: 'center' },
  { id: 'quiz', label: 'Quiz', align: 'center' },
  { id: 'completion', label: 'Combined', align: 'center' },
  { id: 'status', label: 'Status', align: 'center' },
];

function StatCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string | number;
  sub?: string;
}) {
  return (
    <Card sx={{ p: 3, height: '100%' }}>
      <Typography variant="subtitle2" color="text.secondary" noWrap>
        {label}
      </Typography>
      <Typography variant="h3" sx={{ mt: 1 }}>
        {value}
      </Typography>
      {sub ? (
        <Typography variant="caption" color="text.secondary">
          {sub}
        </Typography>
      ) : null}
    </Card>
  );
}

// ----------------------------------------------------------------------

export default function DiplomaReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState<any>(null);
  const [students, setStudents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [filterName, setFilterName] = useState('');
  // FEATURES C2: each batch's real state (the page's state machine, computed by
  // the API) with its start / last day to join, keyed by batch name.
  const [batchRows, setBatchRows] = useState<Record<string, BatchListRow>>({});

  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    setPage,
    onSort,
    onChangePage,
    onChangeRowsPerPage,
  } = useTable({ defaultOrderBy: 'progress', defaultOrder: 'desc' });

  const { visibleColumns, toggleColumn, headLabel } = useColumnVisibility(
    STUDENT_COLUMNS,
    'kayfa.diplomaReport.visibleColumns.v2'
  );

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.get(APIS.ADMIN_STATS.DIPLOMA_REPORT(id as string));
        setData(res.data?.data ?? res.data);
      } catch (e: any) {
        enqueueSnackbar(e?.message || 'Failed to load diploma report', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    })();
    (async () => {
      try {
        const res = await axiosInstance.get(APIS.BATCH_SESSIONS.LIST_BATCHES);
        const payload = res.data?.data ?? res.data;
        const mine: Record<string, BatchListRow> = {};
        (Array.isArray(payload?.items) ? payload.items : [])
          .filter((r: BatchListRow) => String(r.diploma) === String(id))
          .forEach((r: BatchListRow) => {
            mine[r.batchName] = r;
          });
        setBatchRows(mine);
      } catch {
        // No state: the table falls back to active / capacity below.
        setBatchRows({});
      }
    })();
    (async () => {
      setLoadingStudents(true);
      try {
        const res = await axiosInstance.get(APIS.ADMIN_STATS.DIPLOMA_STUDENTS(id as string));
        const payload = res.data?.data ?? res.data;
        setStudents(Array.isArray(payload?.items) ? payload.items : []);
      } catch (e: any) {
        enqueueSnackbar(e?.message || 'Failed to load students', { variant: 'error' });
      } finally {
        setLoadingStudents(false);
      }
    })();
  }, [id, enqueueSnackbar]);

  // The backend sends revenue: null (not []) when the caller has no payments
  // entitlement — customer-care reaches this report because it owns the
  // catalogue, not because it may see money. Null means WITHHELD, so hide the
  // money entirely rather than render it as "—" / "0 sales", which reads as
  // "this diploma earned nothing".
  const showRevenue = data?.revenue != null;

  // Revenue string ("1,200 EGP · $300"); empty when no paid sales.
  const revenueText =
    data?.revenue?.length
      ? data.revenue
          .map((r: any) => `${Number(r.total || 0).toLocaleString()} ${r.currency}`)
          .join(' · ')
      : '—';

  // Client-side sort + search (diploma rosters are modest in size).
  const sortKey = (s: any) => ({
    name: s.name,
    email: s.email,
    phone: s.phone,
    batch: s.batch,
    progress: s.progress,
    status: s.progress,
  });
  const rows = [...students]
    .map((s) => ({ ...s, ...sortKey(s) }))
    .sort(getComparator(order, orderBy))
    .filter(
      (s) =>
        !filterName ||
        s.name?.toLowerCase().includes(filterName.toLowerCase()) ||
        s.email?.toLowerCase().includes(filterName.toLowerCase()) ||
        (s.batch || '').toLowerCase().includes(filterName.toLowerCase())
    );
  const paged = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const isNotFound = !rows.length && !!filterName;
  const isVisible = (k: string) => visibleColumns.includes(k);

  return (
    <Page title="Diploma Report">
      <Container maxWidth="lg">
        <HeaderBreadcrumbs
          heading={data?.diploma?.title ? `Report — ${data.diploma.title}` : 'Diploma Report'}
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            { name: 'Diplomas', href: PATH_DASHBOARD.diplomas },
            { name: 'Report' },
          ]}
        />

        {loading && <LinearProgress sx={{ mb: 3 }} />}

        {data && (
          <Grid container spacing={3} sx={{ mb: 1 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Enrolled"
                value={data.enrolled}
                sub={showRevenue ? `${data.paidCount || 0} paid` : undefined}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Completed"
                value={data.completed}
                sub={`${data.completionRate}% completion`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard label="Avg progress" value={`${data.avgProgress}%`} sub="attendance" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard label="Batches" value={data.batchesCount} />
            </Grid>
            {showRevenue && (
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  label="Revenue"
                  value={revenueText}
                  sub={`${data.paidCount || 0} sales`}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Duration"
                value={data.diploma?.durationWeeks ? `${data.diploma.durationWeeks} wks` : '—'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Price"
                value={data.diploma?.price ? `${Number(data.diploma.price).toLocaleString()} EGP` : '—'}
                sub={data.diploma?.price_usd ? `$${Number(data.diploma.price_usd).toLocaleString()}` : undefined}
              />
            </Grid>
          </Grid>
        )}

        {/* Batches — enrolled vs capacity (seats left) */}
        {data?.batches?.length ? (
          <Card sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ p: 2 }}>
              Batches ({data.batches.length})
            </Typography>
            <Divider />
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Batch</TableCell>
                    <TableCell align="center">State</TableCell>
                    <TableCell>Start</TableCell>
                    <TableCell>Last day to join</TableCell>
                    <TableCell align="center">Enrolled</TableCell>
                    <TableCell align="center">Capacity</TableCell>
                    <TableCell align="center">Seats left</TableCell>
                    <TableCell align="right">Manage</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.batches.map((b: any, i: number) => {
                    const cap = typeof b.capacity === 'number' ? b.capacity : null;
                    const pct = cap ? Math.min(100, Math.round((b.enrolled / cap) * 100)) : 0;
                    const full = cap !== null && b.enrolled >= cap;
                    // Two buyers of the last seat both paid (FEATURES C3): shown in red.
                    const over = cap !== null && cap > 0 && b.enrolled > cap;
                    const live = batchRows[b.name];
                    const chip: { label: string; color: BatchStateColor; hint: string } = live
                      ? batchStateChip(live)
                      : {
                          label: b.active ? (full ? 'Full' : 'Open') : 'Inactive',
                          color: b.active ? (full ? 'warning' : 'success') : 'default',
                          hint: '',
                        };
                    const join = live ? lastDayToJoin(live) : { day: '', isStartDay: false };
                    return (
                      <TableRow key={`${b.name}-${i}`} hover>
                        <TableCell>
                          <Typography variant="subtitle2">{b.name}</Typography>
                        </TableCell>
                        <TableCell align="center" title={chip.hint}>
                          <Label color={chip.color}>{chip.label}</Label>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {formatDay(live?.startDate) || '—'}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {join.day || '—'}
                          {join.isStartDay && (
                            <Typography component="span" variant="caption" color="text.secondary">
                              {' '}(start day)
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={over ? { color: 'error.main', fontWeight: 700 } : undefined}
                          title={over ? 'More students than seats. Nothing is refunded automatically.' : undefined}
                        >
                          {b.enrolled}
                          {over ? ' (over capacity)' : ''}
                        </TableCell>
                        <TableCell align="center">{cap ?? '∞'}</TableCell>
                        <TableCell align="center">
                          {cap !== null ? (
                            <Stack spacing={0.5} alignItems="center" sx={{ minWidth: 120 }}>
                              <Typography variant="body2">{b.seatsLeft}</Typography>
                              <LinearProgress
                                variant="determinate"
                                value={pct}
                                color={full ? 'error' : pct > 80 ? 'warning' : 'success'}
                                sx={{ width: '100%', height: 6, borderRadius: 1 }}
                              />
                            </Stack>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Iconify icon="eva:calendar-outline" />}
                            onClick={() =>
                              navigate(
                                `${PATH_DASHBOARD.diplomas}/${id}/batches/${encodeURIComponent(
                                  b.name,
                                )}`,
                              )
                            }
                          >
                            Manage
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        ) : null}

        {/* Students enrolled in this diploma */}
        <Card sx={{ mt: 3 }}>
          <Stack direction="row" alignItems="center" sx={{ p: 2, gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
              Students ({rows.length})
            </Typography>
            <TextField
              size="small"
              value={filterName}
              onChange={(e) => {
                setFilterName(e.target.value);
                setPage(0);
              }}
              placeholder="Search student / batch..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 240 }}
            />
            <ColumnsButton
              columns={STUDENT_COLUMNS}
              visibleColumns={visibleColumns}
              onToggle={toggleColumn}
            />
          </Stack>

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table size={dense ? 'small' : 'medium'}>
                <TableHeadCustom
                  order={order}
                  orderBy={orderBy}
                  headLabel={headLabel}
                  rowCount={rows.length}
                  onSort={onSort}
                />
                <TableBody>
                  {loadingStudents ? (
                    [...Array(5)].map((_, i) => <TableSkeleton key={`ds-skeleton-${i}`} />)
                  ) : (
                    <>
                      {paged.map((s) => (
                        <TableRow key={s.userId} hover>
                          {isVisible('name') && (
                            <TableCell>
                              <Typography variant="subtitle2" noWrap>
                                {s.name || '—'}
                              </Typography>
                            </TableCell>
                          )}
                          {isVisible('email') && <TableCell>{s.email || '—'}</TableCell>}
                          {isVisible('phone') && <TableCell>{s.phone || '—'}</TableCell>}
                          {isVisible('batch') && (
                            <TableCell>
                              {s.batch ? <Label color="info">{s.batch}</Label> : '—'}
                            </TableCell>
                          )}
                          {isVisible('progress') && (
                            <TableCell align="center">
                              {s.attendancePct ?? 0}%
                              <Typography variant="caption" color="text.secondary" display="block">
                                {s.attendedCount ?? 0}/{s.totalSessions ?? 0} sessions
                              </Typography>
                            </TableCell>
                          )}
                          {isVisible('quiz') && (
                            <TableCell align="center">{s.quizAvg ?? 0}%</TableCell>
                          )}
                          {isVisible('completion') && (
                            <TableCell align="center" sx={{ fontWeight: 600 }}>
                              {s.combined ?? s.completion ?? 0}%
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                sx={{ fontWeight: 400 }}
                              >
                                Rec {s.recordedPct ?? 0}% · Live {s.livePct ?? s.completion ?? 0}%
                              </Typography>
                            </TableCell>
                          )}
                          {isVisible('status') && (
                            <TableCell align="center">
                              <Label color={s.eligible ? 'success' : 'default'}>
                                {s.eligible ? 'Eligible' : 'In progress'}
                              </Label>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                      <TableNoData isNotFound={isNotFound} />
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={rows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={onChangePage}
              onRowsPerPageChange={onChangeRowsPerPage}
            />
          </Box>
        </Card>

        {/* Per-batch attendance grid (present/late/absent per session) +
            completion + CSV export. */}
        {id && (
          <AttendanceMatrixCard
            diplomaId={id}
            batchNames={(data?.batches || []).map((b: any) => b.name)}
          />
        )}

        {/* Recorded-video watch progress per assigned track (real watch-time). */}
        {id && (
          <RecordedProgressCard
            diplomaId={id}
            batchNames={(data?.batches || []).map((b: any) => b.name)}
          />
        )}
      </Container>
    </Page>
  );
}
